import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Sparkles, X, Send, Loader2, ChevronDown, ChevronUp,
  Brain, CheckCircle, BarChart3, AlertCircle, CheckSquare,
  Search, FileText, Zap, Minimize2, Maximize2
} from "lucide-react";
import { sendStreamingChatQueryV2 } from "../api";

// AMA-specific suggested prompts
const AMA_SUGGESTIONS = [
  {
    icon: BarChart3,
    text: "Pipeline overview",
    query: "Show me my current pipeline overview"
  },
  {
    icon: AlertCircle,
    text: "Cases needing attention",
    query: "Which cases need my attention today?"
  },
  {
    icon: CheckSquare,
    text: "Overdue tasks",
    query: "Show me all overdue tasks across my pipeline"
  },
  {
    icon: Search,
    text: "Find a case",
    query: "Find the case for borrower "
  },
  {
    icon: FileText,
    text: "Recent reports",
    query: "What AI reports have completed recently?"
  },
  {
    icon: Zap,
    text: "Quick stats",
    query: "Give me a quick summary of my portfolio performance"
  }
];

/**
 * Parse message content to extract thinking blocks
 * Returns: { cleanContent, thinkingContent }
 */
const parseMessageContent = (content) => {
  if (!content) return { cleanContent: '', thinkingContent: null };

  // Extract <thinking>...</thinking> blocks
  const thinkingRegex = /<thinking>([\s\S]*?)<\/thinking>/gi;
  const thinkingMatches = content.match(thinkingRegex);
  const thinkingContent = thinkingMatches
    ? thinkingMatches.map(m => m.replace(/<\/?thinking>/gi, '').trim()).join('\n')
    : null;

  // Remove thinking blocks from content
  let cleanContent = content.replace(thinkingRegex, '').trim();

  // If cleanContent is empty but we have thinking content, use thinking as main content
  if (!cleanContent && thinkingContent) {
    cleanContent = thinkingContent;
    return { cleanContent, thinkingContent: null }; // Don't show collapsed section
  }

  return { cleanContent, thinkingContent };
};

// Thinking Steps Component
const ThinkingSteps = ({ steps, isCollapsed = false }) => {
  const [expanded, setExpanded] = useState(!isCollapsed);

  useEffect(() => {
    if (isCollapsed) setExpanded(false);
  }, [isCollapsed]);

  if (!steps || steps.length === 0) return null;

  const completedCount = steps.filter(s => s.status === 'completed').length;
  const totalCount = steps.length;

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-violet-500/10 rounded-full border border-violet-500/30 text-violet-400 hover:bg-violet-500/20 transition-colors"
      >
        <Brain className="w-3 h-3" />
        <span>
          {completedCount === totalCount
            ? `Analyzed in ${totalCount} steps`
            : `${completedCount}/${totalCount} steps`}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
    );
  }

  return (
    <div className="p-3 bg-violet-500/10 rounded-xl border border-violet-500/20 animate-fade-in">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Brain className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-xs font-medium text-violet-300 uppercase tracking-wider">
            AI Reasoning
          </span>
        </div>
        <button
          onClick={() => setExpanded(false)}
          className="p-1 rounded hover:bg-violet-500/20 text-violet-400 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="space-y-1.5">
        {steps.map((step) => (
          <div key={step.stepId} className="flex items-center gap-2 py-1 text-sm">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              {step.status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              ) : step.status === 'error' ? (
                <X className="w-4 h-4 text-red-400" />
              ) : (
                <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
              )}
            </div>
            <span className={`flex-1 ${
              step.status === 'completed' ? 'text-slate-400' :
              step.status === 'error' ? 'text-red-400' : 'text-slate-200'
            }`}>
              {step.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to linkify case IDs and task IDs in text
const LinkifyIds = ({ text, onCaseClick, onTaskClick }) => {
  // Debug: Log that we're rendering with handlers
  // (only log if we have case/task patterns in text to avoid spam)
  const hasIds = /(case-[a-z0-9]+|task-[a-z0-9]+)/gi.test(text);
  if (hasIds) {
    console.log('[LinkifyIds] Rendering with handlers - onCaseClick:', typeof onCaseClick, 'onTaskClick:', typeof onTaskClick, 'text sample:', text.substring(0, 50));
  }

  // Pattern to match case IDs (case-xxxxxxxx) and task IDs (task-xxxxxxxx)
  const idPattern = /(case-[a-z0-9]+|task-[a-z0-9]+)/gi;

  const parts = text.split(idPattern);

  return parts.map((part, index) => {
    const lowerPart = part.toLowerCase();

    if (lowerPart.startsWith('case-')) {
      return (
        <button
          key={index}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[LinkifyIds] Case clicked:', part, '| Handler available:', typeof onCaseClick === 'function');
            if (typeof onCaseClick === 'function') {
              console.log('[LinkifyIds] Calling onCaseClick...');
              onCaseClick(part);
              console.log('[LinkifyIds] onCaseClick called successfully');
            } else {
              console.error('[LinkifyIds] onCaseClick is not a function!', onCaseClick);
            }
          }}
          className="text-violet-400 hover:text-violet-300 underline underline-offset-2 decoration-violet-400/50 hover:decoration-violet-300 transition-colors font-medium cursor-pointer"
        >
          {part}
        </button>
      );
    }

    if (lowerPart.startsWith('task-')) {
      return (
        <button
          key={index}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[LinkifyIds] Task clicked:', part);
            onTaskClick?.(part);
          }}
          className="text-yellow-400 hover:text-yellow-300 underline underline-offset-2 decoration-yellow-400/50 hover:decoration-yellow-300 transition-colors font-medium cursor-pointer"
        >
          {part}
        </button>
      );
    }

    return <span key={index}>{part}</span>;
  });
};

const AMAChat = ({ userId, onCaseClick, onTaskClick }) => {
  // Debug: Log when props change
  console.log('[AMAChat] Props received - onCaseClick:', typeof onCaseClick, 'onTaskClick:', typeof onTaskClick);

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [hasResponseStarted, setHasResponseStarted] = useState(false);
  const [sessionId] = useState(() => {
    // Generate or retrieve AMA session ID
    const stored = localStorage.getItem('ama_session_id');
    if (stored) return stored;
    const newId = `ama-${userId}-${Date.now()}`;
    localStorage.setItem('ama_session_id', newId);
    return newId;
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const streamBufferRef = useRef("");
  const streamingMessageIdRef = useRef(null);
  const hasResponseStartedRef = useRef(false);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Smooth streaming effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (streamBufferRef.current.length > 0 && streamingMessageIdRef.current) {
        const backlog = streamBufferRef.current.length;
        const speed = backlog > 100 ? 5 : backlog > 30 ? 3 : 1;
        const nextChunk = streamBufferRef.current.slice(0, speed);
        streamBufferRef.current = streamBufferRef.current.slice(speed);

        setMessages(prev => prev.map(msg => {
          if (msg.id === streamingMessageIdRef.current) {
            return { ...msg, content: msg.content + nextChunk };
          }
          return msg;
        }));
      }
    }, 16);

    return () => clearInterval(interval);
  }, []);

  // Load conversation from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('ama_conversation');
    if (stored) {
      try {
        setMessages(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load AMA conversation:', e);
      }
    }
  }, []);

  // Save conversation to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('ama_conversation', JSON.stringify(messages.slice(-50))); // Keep last 50 messages
    }
  }, [messages]);

  const handleSend = useCallback(async (textOverride = null) => {
    const text = typeof textOverride === 'string' ? textOverride : inputValue;
    if (!text.trim() || isStreaming) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    const assistantMessageId = `assistant-${Date.now() + 1}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInputValue("");
    setIsStreaming(true);
    setThinkingSteps([]);
    setHasResponseStarted(false);
    hasResponseStartedRef.current = false;

    streamBufferRef.current = "";
    streamingMessageIdRef.current = assistantMessageId;

    // Send to API with mode: "ama"
    sendStreamingChatQueryV2(
      {
        message: text.trim(),
        session_id: sessionId,
        mode: "ama",
        case_id: null
      },
      {
        onChunk: (chunk) => {
          streamBufferRef.current += chunk;
          if (!hasResponseStartedRef.current) {
            hasResponseStartedRef.current = true;
            setHasResponseStarted(true);
          }
        },
        onThinkingStep: (step) => {
          setThinkingSteps(prev => {
            const existing = prev.find(s => s.stepId === step.stepId);
            if (existing) {
              return prev.map(s => s.stepId === step.stepId ? step : s);
            }
            return [...prev, step];
          });
        },
        onToolCall: (name, args) => {
          console.log('[AMA] Tool called:', name, args);
        },
        onToolResult: (name, result) => {
          console.log('[AMA] Tool result:', name);
        },
        onComplete: () => {
          const checkDone = setInterval(() => {
            if (streamBufferRef.current.length === 0) {
              clearInterval(checkDone);
              streamingMessageIdRef.current = null;
              setIsStreaming(false);
              setMessages(prev => prev.map(msg => {
                if (msg.id === assistantMessageId) {
                  return { ...msg, isStreaming: false };
                }
                return msg;
              }));
              setTimeout(() => setThinkingSteps([]), 500);
            }
          }, 100);
        },
        onError: (error) => {
          console.error('[AMA] Error:', error);
          streamingMessageIdRef.current = null;
          streamBufferRef.current = "";
          setIsStreaming(false);
          setThinkingSteps([]);
          setMessages(prev => prev.map(msg => {
            if (msg.id === assistantMessageId) {
              return {
                ...msg,
                content: `Sorry, I encountered an error: ${error}`,
                isStreaming: false,
                isError: true
              };
            }
            return msg;
          }));
        }
      }
    );
  }, [inputValue, isStreaming, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    localStorage.removeItem('ama_conversation');
    // Generate new session ID
    const newId = `ama-${userId}-${Date.now()}`;
    localStorage.setItem('ama_session_id', newId);
  };

  // Floating Action Button - positioned above Team Chat button (vertically stacked)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-2xl flex items-center justify-center hover:scale-105 hover:shadow-violet-500/25 transition-all duration-300 ring-4 ring-white dark:ring-slate-900"
        aria-label="Open AMA Assistant"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  // Chat Interface
  return (
    <div
      className={`fixed z-50 transition-all duration-300 ease-out ${
        isMinimized
          ? 'bottom-6 right-20 w-80'
          : 'bottom-6 right-20 w-[420px] max-w-[calc(100vw-96px)]'
      }`}
    >
      <div
        className={`bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${
          isMinimized ? 'h-14' : 'h-[600px] max-h-[calc(100vh-120px)]'
        }`}
        style={{
          boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.25)'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600/20 to-purple-600/20 border-b border-slate-700/50 cursor-pointer"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">AMA Assistant</h3>
              {!isMinimized && (
                <p className="text-xs text-slate-400">Ask anything about your pipeline</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                // Welcome Screen
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Hi! I'm your AMA Assistant
                  </h3>
                  <p className="text-sm text-slate-400 mb-6">
                    I can help you manage your entire pipeline, find cases, track tasks, and more.
                  </p>

                  {/* Quick Actions Grid */}
                  <div className="grid grid-cols-2 gap-2 w-full">
                    {AMA_SUGGESTIONS.slice(0, 4).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSend(suggestion.query)}
                        className="flex items-center gap-2 p-3 text-left bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-violet-500/30 rounded-xl transition-all group"
                      >
                        <suggestion.icon className="w-4 h-4 text-violet-400 group-hover:text-violet-300" />
                        <span className="text-xs text-slate-300 group-hover:text-white">
                          {suggestion.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                // Message List
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                          message.role === 'user'
                            ? 'bg-violet-600 text-white'
                            : message.isError
                              ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                              : 'bg-slate-800 text-slate-100 border border-slate-700/50'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-start gap-2">
                            <div className={`mt-0.5 p-1 rounded-lg shrink-0 ${
                              message.isStreaming ? 'bg-violet-500/20' : 'bg-slate-700/50'
                            }`}>
                              <Sparkles className={`w-3 h-3 ${
                                message.isStreaming ? 'text-violet-400 animate-pulse' : 'text-slate-400'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Show thinking steps while streaming and no content yet */}
                              {message.isStreaming && !message.content && thinkingSteps.length > 0 && (
                                <ThinkingSteps steps={thinkingSteps} isCollapsed={hasResponseStarted} />
                              )}

                              {/* Message content */}
                              {message.content ? (
                                (() => {
                                  const { cleanContent, thinkingContent } = parseMessageContent(message.content);
                                  return (
                                    <div className="leading-relaxed flex-1 break-words min-w-0">
                                      {/* Collapsible thinking section */}
                                      {thinkingContent && (
                                        <details className="mb-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                                          <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs text-violet-300 hover:text-violet-200">
                                            <Brain className="h-3.5 w-3.5 text-violet-400" />
                                            <span>View AI reasoning</span>
                                          </summary>
                                          <div className="px-3 pb-2 text-xs text-slate-400 whitespace-pre-wrap">
                                            {thinkingContent}
                                          </div>
                                        </details>
                                      )}

                                      {/* Main content */}
                                      <div className="whitespace-pre-wrap">
                                        {cleanContent.split('\n').map((line, i) => {
                                          const parts = line.split(/(\*\*.*?\*\*)/g);
                                          return (
                                            <div key={i} className="min-h-[1.2em]">
                                              {parts.map((part, j) => {
                                                if (part.startsWith('**') && part.endsWith('**')) {
                                                  return (
                                                    <strong key={j} className="text-violet-300">
                                                      <LinkifyIds
                                                        text={part.slice(2, -2)}
                                                        onCaseClick={onCaseClick}
                                                        onTaskClick={onTaskClick}
                                                      />
                                                    </strong>
                                                  );
                                                }
                                                return (
                                                  <span key={j}>
                                                    <LinkifyIds
                                                      text={part}
                                                      onCaseClick={onCaseClick}
                                                      onTaskClick={onTaskClick}
                                                    />
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {message.isStreaming && (
                                        <span className="inline-block w-2 h-4 ml-0.5 bg-violet-400 animate-pulse" />
                                      )}
                                    </div>
                                  );
                                })()
                              ) : message.isStreaming ? (
                                <div className="flex items-center gap-2 text-slate-400">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span className="text-xs">Thinking...</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}

                        {message.role === 'user' && (
                          <div className="whitespace-pre-wrap break-words">{message.content}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Suggestions Bar (when there are messages) */}
            {messages.length > 0 && !isStreaming && (
              <div className="px-4 py-2 border-t border-slate-700/50 bg-slate-800/30">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                  {AMA_SUGGESTIONS.slice(0, 3).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(suggestion.query)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700/50 hover:border-violet-500/30 rounded-full text-slate-300 hover:text-white transition-all whitespace-nowrap"
                    >
                      <suggestion.icon className="w-3 h-3 text-violet-400" />
                      {suggestion.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-slate-700/50 bg-slate-900/80">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask me anything..."
                    rows={1}
                    disabled={isStreaming}
                    className="w-full resize-none bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 pr-10 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ minHeight: '44px', maxHeight: '120px' }}
                  />
                </div>
                <button
                  onClick={() => handleSend()}
                  disabled={!inputValue.trim() || isStreaming}
                  className="p-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-violet-500/25"
                >
                  {isStreaming ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-violet-400" />
                  Global pipeline access
                </span>
                {messages.length > 0 && (
                  <button
                    onClick={clearConversation}
                    className="text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    Clear chat
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AMAChat;
